import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import PrintIcon from "@mui/icons-material/Print";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import PrintMode from "../schedule_components/PrintMode";
import PrintModeAdvanced from "../schedule_components/PrintModeAdvanced";
import PrintModeMonthly from "../schedule_components/PrintModeMonthly";
import PrintModeDaily from "../schedule_components/PrintModeDaily";
import { SegmentedControl } from "../schedule_components/print/PrintShell";

const PRINT_MODES = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const style = {
  position: "absolute",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  top: 0,
  left: 0,
  bgcolor: "white",
  outline: "none",
};

export default function PrintButton(props) {
  const [open, setOpen] = React.useState(false);
  const [showAdvancedPrintMode, setShowAdvancedPrintMode] =
    React.useState(false);

  // Print mode follows the schedule view it was opened from, then can be
  // switched freely inside the print view.
  const viewMode = props.monthlyView ? "month" : props.dailyView ? "day" : "week";
  const [printMode, setPrintMode] = React.useState(viewMode);

  const dowInDisplayedMonth = (d) => {
    const date = props.addDays(props.week_start_date, d);
    return (
      date.getMonth() === props.currentMonth &&
      date.getFullYear() === props.currentYear
    );
  };
  const loadedWeekInDisplayedMonth =
    !props.monthlyView ||
    !props.week_start_date ||
    [0, 1, 2, 3, 4, 5, 6].some(dowInDisplayedMonth);

  // From the Month view the schedule's dowSelected is reset to 0, which can
  // sit in the previous month when the loaded week straddles a boundary.
  // Start Day printing on the first loaded day inside the displayed month.
  const defaultDow =
    props.monthlyView && props.week_start_date
      ? [0, 1, 2, 3, 4, 5, 6].find(dowInDisplayedMonth) ?? 0
      : props.dowSelected || 0;

  // Day chosen inside the Day print mode; lives here so switching to Month
  // can anchor on it.
  const [printDow, setPrintDow] = React.useState(0);
  const [monthAnchorDow, setMonthAnchorDow] = React.useState(3);

  const handleOpen = () => {
    setPrintMode(viewMode);
    setShowAdvancedPrintMode(false);
    setPrintDow(defaultDow);
    setMonthAnchorDow(props.dailyView ? defaultDow : 3);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  // Month printing from Day anchors on the day being printed; from Week, on
  // the middle of the week so a week straddling two months lands sensibly.
  const monthAnchor = props.week_start_date
    ? props.addDays(props.week_start_date, monthAnchorDow)
    : new Date();

  // Week-loaded data (schedule, team, notes) only covers the one loaded week.
  // Opening from the Month view after navigating months leaves that week
  // outside the displayed month, so Day/Week would print the wrong week.
  const modeOptions = PRINT_MODES.map((m) =>
    m.value !== "month" && !loadedWeekInDisplayedMonth
      ? {
          ...m,
          disabled: true,
          title: "Open the Week view on a week in this month to print it",
        }
      : m
  );

  const modeSwitcher = (
    <SegmentedControl
      value={printMode}
      onChange={(m) => {
        if (m === "month") setMonthAnchorDow(printMode === "day" ? printDow : 3);
        setPrintMode(m);
        setShowAdvancedPrintMode(false);
      }}
      options={modeOptions}
    />
  );

  const handleMenuItemClick = () => {
    handleOpen();
    props.onMenuItemClick?.();
  };

  return (
    <div>
      {props.renderAsMenuItem ? (
        <MenuItem onClick={handleMenuItemClick} sx={{ py: 1.25 }}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 14 }}>
            Print view
          </ListItemText>
        </MenuItem>
      ) : (
        <Tooltip title={"Print View"}>
          <IconButton onClick={handleOpen}>
            <PrintIcon />
          </IconButton>
        </Tooltip>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div className="bg-white w-full h-full">
            {printMode === "day" ? (
              <PrintModeDaily
                onClose={handleClose}
                scheduleLoading={props.scheduleLoading}
                modeSwitcher={modeSwitcher}
                addDays={props.addDays}
                week_start_date={props.week_start_date}
                dowSelected={printDow}
                onDowChange={setPrintDow}
                actual_schedule={props.actual_schedule}
                notes={props.notes}
                role_names={props.role_names}
                storeNames={props.storeNames}
                store_id={props.store_id}
                printHoursShownEnabled={props.printHoursShownEnabled}
                selectedRolesForFiltering={props.selectedRolesForFiltering}
              />
            ) : printMode === "month" ? (
              <PrintModeMonthly
                onClose={handleClose}
                modeSwitcher={modeSwitcher}
                // From the week or day view, print the month that week is in.
                currentMonth={
                  props.monthlyView ? props.currentMonth : monthAnchor.getMonth()
                }
                currentYear={
                  props.monthlyView ? props.currentYear : monthAnchor.getFullYear()
                }
                store_id={props.store_id}
                storeNames={props.storeNames}
                role_names={props.role_names}
                businessWeekStartDayIndex={props.businessWeekStartDayIndex}
                selectedRolesForFiltering={props.selectedRolesForFiltering}
              />
            ) : showAdvancedPrintMode ? (
              <PrintModeAdvanced
                onClose={handleClose}
                scheduleLoading={props.scheduleLoading}
                modeSwitcher={modeSwitcher}
                addDays={props.addDays}
                week_start_date={props.week_start_date}
                store_team_member_weekly_info_view={
                  props.store_team_member_weekly_info_view
                }
                notes={props.notes}
                actual_schedule={props.actual_schedule}
                storeNames={props.storeNames}
                store_id={props.store_id}
                role_names={props.role_names}
                filter_df_to_day={props.filter_df_to_day}
                setShowAdvancedPrintMode={setShowAdvancedPrintMode}
              />
            ) : (
              <PrintMode
                onClose={handleClose}
                scheduleLoading={props.scheduleLoading}
                modeSwitcher={modeSwitcher}
                addDays={props.addDays}
                week_start_date={props.week_start_date}
                store_team_member_weekly_info={props.store_team_member_weekly_info}
                store_team_member_weekly_info_view={
                  props.store_team_member_weekly_info_view
                }
                get_t_str={props.get_t_str}
                notes={props.notes}
                actual_schedule={props.actual_schedule}
                role_names={props.role_names}
                filter_df_to_day={props.filter_df_to_day}
                printHoursShownEnabled={props.printHoursShownEnabled}
                storeNames={props.storeNames}
                store_id={props.store_id}
                setShowAdvancedPrintMode={setShowAdvancedPrintMode}
                selectedRolesForFiltering={props.selectedRolesForFiltering}
              />
            )}
          </div>
        </Box>
      </Modal>
    </div>
  );
}
